
import { getSharedAudioContext } from "./memoryService";
import { AudioSessionManager } from "./audioSessionManager";

export type AudioMode = 'CALIBRATION' | 'ANALYSIS';

export interface AudioGraph {
    ctx: AudioContext;
    audioStream: MediaStream;
    videoStream?: MediaStream;
    source: MediaStreamAudioSourceNode;
    preGain: GainNode;
    meterAnalyser: AnalyserNode;
    stftAnalyser: AnalyserNode;
    isReady: boolean;
    deviceId_used: string;
    mode: AudioMode;
    effectiveSampleRate: number;
    effectiveLatency: number;
}

let audioGraph: AudioGraph | null = null;

export const getUnifiedAudioVideoGraph = async (
    mode: AudioMode = 'ANALYSIS', 
    audioDeviceId?: string, 
    videoDeviceId?: string,
    includeVideo: boolean = false
): Promise<AudioGraph> => {
    
    if (audioGraph) {
        await disposeAudioGraph();
    }

    const ctx = await getSharedAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();

    const audioConstraint = (audioDeviceId && audioDeviceId !== 'default') 
        ? { deviceId: { ideal: audioDeviceId } } 
        : { deviceId: { ideal: 'default' } };

    const videoConstraint = includeVideo 
        ? ((videoDeviceId && videoDeviceId !== 'default') 
            ? { deviceId: { ideal: videoDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" }
            : { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" })
        : false;

    const constraints: MediaStreamConstraints = {
        audio: {
            ...audioConstraint,
            echoCancellation: { exact: false },
            noiseSuppression: { exact: false },
            autoGainControl: { exact: false },
            // @ts-ignore
            googEchoCancellation: false,
            channelCount: { ideal: 1 }
        },
        video: videoConstraint
    };

    try {
        // Utilisation du gestionnaire de session singleton (Axe B)
        const fullStream = await AudioSessionManager.acquireStream(constraints);
        
        const audioStream = new MediaStream(fullStream.getAudioTracks());
        const videoStream = includeVideo ? new MediaStream(fullStream.getVideoTracks()) : undefined;

        const source = ctx.createMediaStreamSource(audioStream);
        const preGain = ctx.createGain();
        preGain.gain.value = 1.0;

        const meterAnalyser = ctx.createAnalyser();
        meterAnalyser.fftSize = 1024;
        meterAnalyser.smoothingTimeConstant = 0.3;

        const stftAnalyser = ctx.createAnalyser();
        stftAnalyser.fftSize = 2048;

        source.connect(preGain);
        preGain.connect(meterAnalyser);
        preGain.connect(stftAnalyser);

        audioGraph = {
            ctx, audioStream, videoStream, source, preGain, meterAnalyser, stftAnalyser,
            isReady: true, deviceId_used: audioDeviceId || 'default', mode,
            effectiveSampleRate: ctx.sampleRate,
            effectiveLatency: ctx.baseLatency || 0
        };

        return audioGraph;
    } catch (err: any) {
        if (audioDeviceId || videoDeviceId) {
            return getUnifiedAudioVideoGraph(mode, undefined, undefined, includeVideo);
        }
        throw err;
    }
};

export const disposeAudioGraph = async () => {
    if (audioGraph) {
        try {
            audioGraph.source.disconnect();
            audioGraph.preGain.disconnect();
            audioGraph.meterAnalyser.disconnect();
            audioGraph.stftAnalyser.disconnect();
            AudioSessionManager.releaseStream();
        } catch (e) {}
        audioGraph = null;
    }
};
